import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Cuántas columnas pide algo. El techo es TRES: a partir de ahí, en el
 * ancho que deja la barra lateral, cada columna baja de los 300 px y un
 * campo de texto deja de admitir una frase de un vistazo.
 */
export type FormColumns = 1 | 2 | 3 | 4;

/**
 * ═══════════════════════════════════════════════════════════════════
 * La rejilla de los formularios largos, declarada UNA vez.
 *
 * Antes cada bloque escribía su propia escalera (`sm:grid-cols-2` aquí,
 * `sm:col-span-2` allá, `grid-cols-1 sm:grid-cols-2` en el panel de
 * idioma). Tres copias de la misma fórmula son tres sitios donde el día
 * que aparezca una tercera columna dos se quedan en dos, y nadie lo ve
 * hasta que un campo se descuelga solo al final de una fila.
 *
 * Aquí hay tres piezas y ninguna sabe de productos:
 *
 *  · `FormGrid`         — los CAMPOS de un bloque.
 *  · `FormRow`          — un campo que ocupa la fila entera.
 *  · `FormSectionStack` — los BLOQUES de un paso, uno DEBAJO de otro.
 *
 * ── Las tarjetas se apilan, los campos se reparten ──────────────────
 * Es la regla del sistema, y es de Anahí (26-ago): "no deben estar dos
 * cards en dos filas, solo los componentes deben acomodarse en máximo
 * en 3 filas". O sea: una tarjeta SIEMPRE ocupa el ancho entero y va
 * debajo de la anterior; dentro de ella, los campos sí se reparten hasta
 * en tres columnas.
 *
 * ── Qué había antes, y por qué se fue ───────────────────────────────
 * Existía `FormSectionGrid`, que ponía los bloques en columnas para que
 * un paso midiera lo del bloque más alto y no la suma. Ahorraba alto y
 * costaba lectura: dos tarjetas al lado se leen como dos formularios
 * paralelos, no se sabe cuál va primero, y el tabulador recorre una
 * entera antes de saltar a la de al lado, que es lo contrario de lo que
 * el ojo espera.
 *
 * Se ha ido ENTERO, con su `span` y su `sectionSpan`, en vez de quedarse
 * como una propiedad que nadie debe usar: una escalera de columnas
 * disponible es una escalera que alguien vuelve a usar. Lo que se pierde
 * en alto se recupera en los campos, que ahora sí llegan a tres
 * columnas dentro de su tarjeta.
 *
 * ── Y por qué `xl` para la tercera columna, no `lg` ─────────────────
 * Los puntos de corte de Tailwind miden la VENTANA, no el hueco. En el
 * panel, la barra lateral se lleva ~270 px, así que a 1024 de ventana el
 * formulario tiene unos 700: tres columnas ahí son 220 px cada una. A
 * 1280 quedan ~950 y las tres columnas pasan de 300, que es donde una
 * etiqueta y su valor vuelven a leerse de corrido.
 * ═══════════════════════════════════════════════════════════════════
 */
const fieldColumns: Record<FormColumns, string> = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 xl:grid-cols-3",
  // Cuatro números cortos (peso, largo, ancho, alto): a 950 px de hueco
  // son ~220 px cada uno, y un número de tres cifras cabe de sobra.
  4: "sm:grid-cols-2 xl:grid-cols-4",
};

export interface FormGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Campos por fila en pantalla ancha. Uno en móvil, siempre. */
  columns?: FormColumns;
  children: ReactNode;
}

/**
 * La rejilla de campos de un bloque.
 *
 * `items-start` y no `items-stretch`: un campo con pista de dos renglones
 * al lado de uno sin pista estiraría al segundo hasta igualarlo, y su
 * caja quedaría más alta que el resto de la columna sin motivo.
 */
export function FormGrid({ columns = 2, children, className, ...rest }: FormGridProps) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 items-start gap-4",
        fieldColumns[columns],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface FormRowProps {
  children: ReactNode;
  className?: string;
}

/**
 * Un campo —o un grupo— que ocupa la FILA ENTERA de la rejilla.
 *
 * Es para lo que nace ancho: una descripción, un aviso legal, una lista
 * que crece, una tabla de precios. Metidos en un tercio de fila, un
 * `<textarea>` de cinco renglones se vuelve una rendija y una lista
 * repetible pierde los botones de subir y bajar al otro lado del salto de
 * línea.
 *
 * `col-span-full` y no `sm:col-span-2`: "todas las columnas que haya" no
 * depende de cuántas haya. Con el número escrito a mano, el día que el
 * bloque pase a tres columnas el campo ancho se queda ocupando dos y deja
 * un hueco a su derecha — que es exactamente lo que pasaba aquí.
 */
export function FormRow({ children, className }: FormRowProps) {
  return <div className={cn("col-span-full grid gap-4", className)}>{children}</div>;
}

export interface FormSectionStackProps {
  children: ReactNode;
  className?: string;
}

/**
 * Los BLOQUES de un paso, uno DEBAJO de otro y a ancho completo.
 *
 * No admite columnas, y eso es lo que es: si aceptara un `columns`, la
 * regla —"nunca dos tarjetas en paralelo"— sería una recomendación. Lo
 * único que hace es separarlas con el mismo hueco que usan los campos,
 * para que la distancia entre dos tarjetas y la distancia entre dos
 * campos no se decidan en sitios distintos.
 *
 * Un paso de una sola tarjeta puede devolverla suelta; envolverla aquí
 * tampoco molesta, y deja el paso preparado para la segunda.
 */
export function FormSectionStack({ children, className }: FormSectionStackProps) {
  return <div className={cn("grid grid-cols-1 gap-4", className)}>{children}</div>;
}
